import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { GetPictureService } from "@app/shared/services/files/getPictures.service";
import { getDriverDisplayName } from "@app/shared/utils/driver-name.util";

export interface DriverCardData {
  name: string;
  lastName: string;
  preferredName?: string | null;
  driverNo: string | null;
  picture: string | null;
  specialNote: string | null;
  cellphone: string | null;
  currentLocation: string | null;
  autoTransmissionOnly: boolean;
  boardName: string | null;
  billToClientName: string | null;
  peopleNetId: string | null;
  tractorId: string | null;
  tractorNumber: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  homeLocation: string | null;
  teamMembers: string[] | null;
  /** Clients this driver is banned from (names) - returned by api/board/driverDetail. */
  bannedClients?: string[];
  /** Client IDs this driver is banned from - returned by api/board/driverDetail. */
  bannedClientIds?: string[];
}

@Component({
  selector: "app-driver-card",
  templateUrl: "./driver-card.component.html",
  styleUrls: ["./driver-card.component.css"],
})
export class DriverCardComponent implements OnChanges, OnDestroy {
  @Input() driver!: DriverCardData;
  @Input() inline: boolean = false;
  @Input() tooltipPosition: "right" | "left" | "top" | "bottom" = "right";

  tooltipHtml: string = "";
  pictureUrl: string = "";
  displayName: string = "";
  copied: boolean = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private getPictureService: GetPictureService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.driver) return;
    if (!changes["driver"]) return;
    this.pictureUrl = this.driver.picture
      ? this.getPictureService.getPicturePath(this.driver.picture, "driver")
      : "assets/img/tractordriver.jpg";
    this.displayName = getDriverDisplayName(this.driver);
    if (!this.inline) {
      this.tooltipHtml = this.buildTooltipHtml();
    }
  }

  ngOnDestroy(): void {
    if (this.copiedTimer) {
      clearTimeout(this.copiedTimer);
      this.copiedTimer = null;
    }
  }

  formatPhone(phone: string | null): string {
    if (!phone) return "";
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
    return phone;
  }

  shortenLocation(loc: string | null): string {
    if (!loc) return "";
    const cleanLoc = loc.trim().replace(/;/g, "").trim();
    const parts = cleanLoc.split(",");
    if (parts.length >= 2) {
      return `${parts[0].trim()}, ${parts[1].trim().split(" ")[0]}`;
    }
    return cleanLoc;
  }

  copyToClipboard(): void {
    const d = this.driver;
    const lines: string[] = [];

    const header = `${this.displayName}${d.driverNo ? ` (#${d.driverNo})` : ""}`;
    lines.push(header);

    if (d.boardName || d.billToClientName) {
      const orgParts: string[] = [];
      if (d.boardName) orgParts.push(`Board: ${d.boardName}`);
      if (d.billToClientName) orgParts.push(`Bill To: ${d.billToClientName}`);
      lines.push(orgParts.join("  |  "));
    }

    if (d.cellphone) lines.push(`Phone: ${this.formatPhone(d.cellphone)}`);
    if (d.currentLocation)
      lines.push(`Current Location: ${this.shortenLocation(d.currentLocation)}`);
    if (d.homeLocation) lines.push(`Home: ${this.shortenLocation(d.homeLocation)}`);

    const equipment: string[] = [];
    if (d.tractorNumber) equipment.push(`Tractor: ${d.tractorNumber}`);
    if (d.trailerNumber) equipment.push(`Trailer: ${d.trailerNumber}`);
    if (d.autoTransmissionOnly) equipment.push("Auto Transmission Only");
    if (equipment.length) lines.push(equipment.join("  |  "));

    if (d.teamMembers?.length) lines.push(`Team: ${d.teamMembers.join(", ")}`);
    if (d.specialNote) lines.push(`Note: ${d.specialNote}`);

    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => {
        this.copied = true;
        if (this.copiedTimer) clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => {
          this.copied = false;
          this.copiedTimer = null;
        }, 2000);
      })
      .catch(() => {});
  }

  private escapeHtml(text: string | null | undefined): string {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private buildTooltipHtml(): string {
    const d = this.driver;
    const esc = (v: string | null | undefined) => this.escapeHtml(v);

    let html = `<div class="clean-driver-tooltip">`;

    html += `<div class="tooltip-top-section">
               <div class="tooltip-avatar">
                 <img src="${esc(this.pictureUrl)}" onerror="this.src='assets/img/tractordriver.jpg'" />
               </div>
               <div class="tooltip-main-info">
                 <div class="tooltip-name-row">
                   <span class="tooltip-name">${esc(this.displayName)}</span>
                   ${d.driverNo ? `<span class="tooltip-driver-no">${esc(d.driverNo)}</span>` : ""}
                 </div>
                 ${d.boardName ? `<div class="tooltip-board-line">${esc(d.boardName)}</div>` : ""}
                 ${d.billToClientName ? `<div class="tooltip-board-line tooltip-billto-line">${esc(d.billToClientName)}</div>` : ""}
               </div>
             </div>`;

    const hasContact = d.currentLocation || d.cellphone || d.homeLocation;
    if (hasContact) {
      html += `<div class="tooltip-contact-row">`;
      if (d.currentLocation) {
        html += `<div class="contact-item"><i class="fa fa-map-marker"></i> ${esc(this.shortenLocation(d.currentLocation))}</div>`;
      }
      if (d.cellphone) {
        html += `<div class="contact-item"><i class="fa fa-phone"></i> ${esc(this.formatPhone(d.cellphone))}</div>`;
      }
      if (d.homeLocation) {
        html += `<div class="contact-item"><i class="fa fa-home"></i> ${esc(this.shortenLocation(d.homeLocation))}</div>`;
      }
      html += `</div>`;
    }

    const hasEquipment = d.tractorNumber || d.trailerNumber || d.autoTransmissionOnly;
    const hasTeam = d.teamMembers && d.teamMembers.length > 0;
    if (hasEquipment || hasTeam) {
      html += `<div class="tooltip-mid-section">`;
      if (d.tractorNumber)
        html += `<span class="eq-badge tractor"><i class="fa fa-truck"></i> ${esc(d.tractorNumber)}</span>`;
      if (d.trailerNumber)
        html += `<span class="eq-badge trailer"><i class="fa fa-subway"></i> ${esc(d.trailerNumber)}</span>`;
      if (d.autoTransmissionOnly) html += `<span class="eq-badge eq-auto">AUTO</span>`;
      if (hasTeam)
        html += `<span class="eq-badge team"><i class="fa fa-users"></i> ${esc(d.teamMembers!.join(", "))}</span>`;
      html += `</div>`;
    }

    if (d.specialNote) {
      html += `<div class="note-text"><i class="fa fa-info-circle"></i> ${esc(d.specialNote)}</div>`;
    }

    html += `</div>`;
    return html;
  }
}
